import { FunctionComponent, ReactNode, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import bottomStyles from "./styles/ActionSheetBottomSheet.module.css";
import sideStyles from "./styles/ActionSheetSideSheet.module.css";
import { LabelPairedXmarkMdBoldIcon } from "@deriv/quill-icons";
import SearchField from "../input/search-field";
import { useDevice } from "../../hooks/useDevice";
import useScrollDisable from "../../hooks/useScrollDisable";
import { motion, AnimatePresence } from "framer-motion";
import BasicButton, { BasicButtonType } from "../buttons/basic";
import IconButton, { IconButtonType } from "../buttons/icon";

export type ActionSheetType = {
  className?: string;
  description?: string;
  title?: string;
  showButtonClose?: boolean;
  controller?: ReactNode;
  content?: ReactNode;
  showHandleBar?: boolean;
  showHeader?: boolean;
  showSearchBar?: boolean;

  /* Button props */
  buttonPrimary?: BasicButtonType;
  buttonSecondary?: BasicButtonType;

  /* Event handlers */
  onClose?: () => void;
  onSearch?: (searchValue: string) => void;

  /* Search props */
  searchValue?: string;
  searchPlaceholder?: string;

  /* Icon props */
  buttonIcon?: IconButtonType;

  /** Variant props */
  type?: "auto" | "bottom" | "side";

  /** Display props */
  show?: boolean;
};

const getStyleForType = (type: string) => {
  return type === "side" ? sideStyles : bottomStyles;
};

export const ActionSheet: FunctionComponent<ActionSheetType> = ({
  className = "",
  type: defaultType = "auto",
  description = "Description",
  title = "Title",
  showButtonClose = true,
  controller,
  content,
  showHandleBar = true,
  showHeader = true,
  showSearchBar = true,
  buttonPrimary,
  buttonSecondary,
  onClose,
  onSearch,
  searchValue: externalSearchValue,
  searchPlaceholder = "Search",
  buttonIcon,
  show = true,
}) => {
  const { isDesktop, isDeviceDetected } = useDevice();
  const [type, setType] = useState(defaultType);
  const [searchValue, setSearchValue] = useState(externalSearchValue || "");
  const styles = getStyleForType(type);

  // Update search value if external value changes
  useEffect(() => {
    if (externalSearchValue !== undefined) {
      setSearchValue(externalSearchValue);
    }
  }, [externalSearchValue]);

  // Update type based on device and defaultType
  useEffect(() => {
    if (isDeviceDetected) {
      if (defaultType === "auto") {
        setType(isDesktop ? "side" : "bottom");
      } else if (defaultType !== type) {
        setType(defaultType);
      }
    }
  }, [isDesktop, isDeviceDetected, defaultType]);

  // Disable body scrolling when the action sheet is shown
  useScrollDisable(show);

  // If show is false, don't render anything
  if (!show) {
    return null;
  }

  const actionSheetContent = (
    <motion.div
      className={[
        type === "bottom" ? styles.typehugContent : styles.actionSheetSideSheet,
        className,
        type === "side" ? "quill-ui-next-actionsheet-full-height" : "",
        type === "bottom" ? "quill-ui-next-actionsheet-full-width" : "",
      ].join(" ")}
      data-type={type}
      {...(type === "bottom"
        ? {
            initial: { y: "100%" },
            animate: { y: 0 },
            exit: { y: "100%" },
            transition: {
              type: "spring",
              damping: 30,
              stiffness: 1000,
              mass: 0.1,
              duration: 0.3,
            },
            drag: "y" as const,
            dragConstraints: { top: 0, bottom: 0 },
            dragElastic: { top: 0, bottom: 0.5 },
            onDragEnd: (_, info: any) => {
              if (info.offset.y > 150 && onClose) {
                onClose();
              }
            },
          }
        : {})}
    >
      {showHandleBar && type === "bottom" && (
        <div className={styles.containerHandleBar}>
          <div className={styles.handleBar} />
        </div>
      )}
      {showHeader && (
        <div className={styles.containerHeader}>
          {type === "side" && (
            <div className={styles.iconTitleCancel}>
              <div className={styles.containerButtonIcon}>
              {buttonIcon && (
                  <IconButton
                    {...buttonIcon}
                    onClick={onClose}
                    color="blackWhite"
                    size="lg"
                    style="tertiary"
                  />
                )}
              </div>
              <div className={styles.title}>{title}</div>
              <div className={styles.containerButtonClose}>
                {showButtonClose && (
                  <IconButton
                    icon={<LabelPairedXmarkMdBoldIcon />}
                    onClick={onClose}
                    color="blackWhite"
                    size="lg"
                    style="tertiary"
                  />
                )}
              </div>
            </div>
          )}
          {type === "bottom" && (
            <div className={styles.titleIcon}>
              <div className={styles.spacer} />
              <div className={styles.title}>{title}</div>
              <div className={styles.containerButtonIcon}>
                {buttonIcon && (
                  <IconButton
                    {...buttonIcon}
                    onClick={onClose}
                    color="blackWhite"
                    size="lg"
                    style="tertiary"
                  />
                )}
              </div>
            </div>
          )}
          {description && (
            <div className={styles.containerDescription}>
              <div className={styles.description}>{description}</div>
            </div>
          )}
        </div>
      )}
      {showSearchBar && (
        <div className={styles.containerSearchBar}>
          <div className={styles.searchBar}>
            <SearchField
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(value) => {
                setSearchValue(value.toString());
                onSearch?.(value.toString());
              }}
              className="quill-ui-next-actionsheet-input"
              size="sm"
            />
          </div>
        </div>
      )}
      {controller && (
        <div className={styles.containerController}>
          <div
            className={`${styles.controller} quill-ui-next-actionsheet-content`}
          >
            {controller}
          </div>
        </div>
      )}
      {content && (
        <div
          className={`${styles.containerContent} quill-ui-next-actionsheet-content-container`}
        >
          <div
            className={`${styles.content} quill-ui-next-actionsheet-content`}
          >
            {content}
          </div>
        </div>
      )}
      {(buttonPrimary || buttonSecondary) && (
        <div className={styles.actionButtons}>
          {buttonPrimary && (
            <BasicButton
              {...buttonPrimary}
              style="primary"
              width="fill container"
              color="blackWhite"
            />
          )}
          {buttonSecondary && (
            <BasicButton
              {...buttonSecondary}
              style="secondary"
              width="fill container"
              color="blackWhite"
            />
          )}
        </div>
      )}
    </motion.div>
  );

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose?.();
    }
  };

  return createPortal(
    <AnimatePresence mode="wait">
      <div
        className={`quill-ui-next-overlay type-${type}`}
        onClick={handleOverlayClick}
      >
        {actionSheetContent}
      </div>
    </AnimatePresence>,
    document.body
  );
};

ActionSheet.displayName = "ActionSheet";

export default ActionSheet;


